import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { useAuth } from '../hooks/useAuth';

interface SignInFormData {
  workEmail: string;
  password: string;
}

interface SignInFormErrors {
  workEmail?: string;
  password?: string;
}

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, user } = useAuth();
  const [formData, setFormData] = useState<SignInFormData>({
    workEmail: '',
    password: '',
  });
  const [errors, setErrors] = useState<SignInFormErrors>({});
  const [focusedField, setFocusedField] = useState<keyof SignInFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const validateEmail = (email: string) => {
    if (!email) return 'Email is required';
    if (!email.includes('@')) return 'Invalid email format';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    // Validate fields
    const validationErrors: SignInFormErrors = {};
    validationErrors.workEmail = validateEmail(formData.workEmail);
    
    // Filter out empty error messages
    const actualErrors = Object.fromEntries(
      Object.entries(validationErrors).filter(([_, value]) => value !== '')
    );

    if (Object.keys(actualErrors).length > 0) {
      setErrors(actualErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await signIn(formData.workEmail, formData.password);
      // Successful login will trigger the useEffect to redirect
    } catch (error) {
      console.error('Sign in error:', error);
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : 'An error occurred during sign in'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when typing
    if (errors[name as keyof SignInFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Marketing Side */}
      <div className="hidden md:flex md:w-1/3 bg-[#F55D3E] items-center justify-center p-12">
        <div className="max-w-md text-white space-y-8">
          <h1 className="text-4xl font-bold">
            IRB reviews in 12 business hours or less, guaranteed.
          </h1>
        </div>
      </div>
      <div className="md:w-2/3 bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo className="w-auto h-12" externalUrl="https://tempusirb.com" />
          </div>
          <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-700">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/signup" className="font-medium text-[#F55D3E] hover:text-[#F9A594]">
              create a new account
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {submitError && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {submitError}
              </div>
            )}
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="workEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Work Email
                </label>
                <input
                  type="email"
                  id="workEmail"
                  name="workEmail"
                  autoComplete="email"
                  value={formData.workEmail}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('workEmail')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${
                    errors.workEmail ? 'border-red-500' : ''
                  }`}
                  required
                />
                {errors.workEmail && (
                  <p className="mt-1 text-sm text-red-500">{errors.workEmail}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-[#F55D3E] text-white py-2 px-4 rounded-lg hover:bg-[#F9A594] transition-colors duration-200 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="mt-4 text-center">
              <Link 
                to="/magic-link-request" 
                className="text-[#F55D3E] hover:text-[#F9A594] text-sm"
              >
                Request Magic Link
              </Link>
            </p>
          </div>
        </div>
      </div>  
    </div>
  );
};

export default SignInPage;
